import { Stack, Typography } from '@mui/material'
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo
} from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useBoolean, useEffectOnce } from 'react-use'

import PageSection from 'components/common/PageSection'
import { AWSAuthenticationType } from 'components/status-checks/authentication-form-utils'
import { AWSAuthenticationAccountSelect } from 'components/status-checks/form/authentication/aws/AWSAuthenticationAccountSelect'
import { AWSAuthenticationTypeSelect } from 'components/status-checks/form/authentication/aws/AWSAuthenticationTypeSelect'
import { AWSIAMRoleSelect } from 'components/status-checks/form/authentication/aws/AWSIAMRoleSelect'
import { AWSServiceAccountAuthentication } from 'components/status-checks/form/authentication/aws/AWSServiceAccountAuthentication'
import { ManagedPolicies } from 'components/status-checks/form/authentication/aws/steps/ManagedPolicies'
import { PermissionPolicy } from 'components/status-checks/form/authentication/aws/steps/PermissionPolicy'
import { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { TeamExternalIntegrationAuthenticationStatus } from 'types/api/status-checks/status-check'
import { isPrivateEdition } from 'utils/app-config'
import { messageFromResponseError } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const ObservabilityToolAWSAuthenticationDetails: React.FC<{
  isAuthenticationValid: boolean
  setIsAuthenticationValid: Dispatch<SetStateAction<boolean>>
}> = ({ isAuthenticationValid, setIsAuthenticationValid }) => {
  const isAwsAuthIamRoleEnabled = !isPrivateEdition()
  const [isCreatingNewAccount, setIsCreatingNewAccount] = useBoolean(false)
  const { getAvailableExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()
  const {
    isCreatingAuthenticationOnly,
    isFetchingAwsIAMRoleMetadata,
    hasErrorAwsIAMRoleMetadata,
    awsIAMRoleMetadataError,
    isEdit,
    isOnboarding
  } = useContext(ObservabilityToolAuthenticationContext)

  const { control, setValue } = useFormContext<StatusCheckFormValues>()

  const [
    awsAuthenticationType,
    awsRoleCreationMethod,
    selectedObservabilityTool,
    externalIntegration
  ] = useWatch({
    control,
    name: [
      'awsAuthenticationType',
      'awsRoleCreationMethod',
      'observabilityTool',
      'externalIntegration'
    ]
  })

  const externalIntegrations = getAvailableExternalIntegrations(false)

  const hasAuthenticatedAWSAccounts = useMemo(() => {
    return externalIntegrations.some(
      (externalIntegration) =>
        externalIntegration.type === IntegrationType.AWS &&
        externalIntegration.lastAuthenticationStatus ===
          TeamExternalIntegrationAuthenticationStatus.Authenticated
    )
  }, [externalIntegrations])

  const domains = selectedObservabilityTool?.domains

  const shouldShowAWSAuthenticationAccountSelect = useMemo(() => {
    if (isEdit || isCreatingAuthenticationOnly) {
      return false
    }

    if (hasAuthenticatedAWSAccounts) {
      return true
    }

    return Boolean(domains?.length)
  }, [
    domains?.length,
    hasAuthenticatedAWSAccounts,
    isCreatingAuthenticationOnly,
    isEdit
  ])

  // Detects if the conditions are met to create a new account
  useEffect(() => {
    if (!isAwsAuthIamRoleEnabled) {
      setValue('awsAuthenticationType', AWSAuthenticationType.ServiceAccount)
    }

    // If the conditions are met to create a new account, set isCreatingNewAccount to true
    if (!isCreatingNewAccount && !isEdit && domains?.length === 0) {
      setIsCreatingNewAccount(true)
    }
  }, [
    domains?.length,
    isAwsAuthIamRoleEnabled,
    isCreatingNewAccount,
    isEdit,
    setIsCreatingNewAccount,
    setValue
  ])

  useEffectOnce(() => {
    if (!isEdit) return

    if (
      isAwsAuthIamRoleEnabled &&
      Boolean(externalIntegration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN)
    ) {
      setValue('awsAuthenticationType', AWSAuthenticationType.IAMRole)
      return
    }

    setValue('awsAuthenticationType', AWSAuthenticationType.ServiceAccount)
  })

  return (
    <PageSection
      errorMessage={messageFromResponseError(
        awsIAMRoleMetadataError ?? null,
        'There was an error retrieving the AWS IAM Role metadata. Please try again.'
      )}
      hasError={hasErrorAwsIAMRoleMetadata}
      isLoading={isFetchingAwsIAMRoleMetadata}
    >
      <Stack spacing={2}>
        <Stack spacing={2}>
          {!isEdit && !isOnboarding && (
            <Typography mt={2} variant="h6">
              Authentication
            </Typography>
          )}
          {shouldShowAWSAuthenticationAccountSelect && (
            <AWSAuthenticationAccountSelect
              domains={domains ?? []}
              isAuthenticationValid={isAuthenticationValid}
              isCreatingNewAccount={isCreatingNewAccount}
              setIsAuthenticationValid={setIsAuthenticationValid}
              setIsCreatingNewAccount={setIsCreatingNewAccount}
            />
          )}
        </Stack>
        {(isCreatingNewAccount || isEdit || isCreatingAuthenticationOnly) && (
          <Stack spacing={2}>
            {isAwsAuthIamRoleEnabled && (
              <>
                <AWSAuthenticationTypeSelect />
                {awsRoleCreationMethod &&
                  awsAuthenticationType === AWSAuthenticationType.IAMRole && (
                    <AWSIAMRoleSelect
                      awsRoleCreationMethod={awsRoleCreationMethod}
                    />
                  )}
              </>
            )}
            {awsAuthenticationType === AWSAuthenticationType.ServiceAccount && (
              <>
                <AWSServiceAccountAuthentication />
                <Stack sx={{ pl: 1 }}>
                  <PermissionPolicy badeCount={1} />
                  <ManagedPolicies badgeCount={2} />
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </PageSection>
  )
}
